import { useEffect, useRef } from "react";
import { useDataProvider, useGetIdentity, useGetList } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import type { Company } from "../types";
import { useConfigurationContext } from "./ConfigurationContext";

// Sikrer at byrået har én "Min bedrift"-rad (companies.is_own_company = true).
// Opprettes automatisk første gang appen lastes hvis den mangler. Den unike
// partielle indeksen (se migrasjon) hindrer duplikater ved samtidige forsøk.
export const useEnsureOwnCompany = () => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const { companyName } = useConfigurationContext();
  const creating = useRef(false);

  const { data, isPending } = useGetList<Company>("companies", {
    filter: { is_own_company: true },
    pagination: { page: 1, perPage: 1 },
  });

  useEffect(() => {
    if (isPending || creating.current) return;
    if (!identity) return;
    if (data && data.length > 0) return;

    creating.current = true;
    dataProvider
      .create("companies", {
        data: {
          name: companyName?.trim() || "Min bedrift",
          is_own_company: true,
          sales_id: identity.id,
        },
      })
      // Oppdater alle companies-spørringer (bl.a. kundekolonnen) slik at
      // "Min bedrift" vises umiddelbart.
      .then(() => queryClient.invalidateQueries({ queryKey: ["companies"] }))
      .catch(() => {
        // La en senere lasting prøve igjen (f.eks. ved race mot unik indeks).
        creating.current = false;
      });
  }, [data, isPending, identity, companyName, dataProvider, queryClient]);
};
